<template>
  <nav class="snav" aria-label="주요 메뉴">
    <div class="snav__brand">
      <span class="snav__mark" aria-hidden="true">i</span>
      <span class="snav__wordmark">IRUDA <span>WORK</span></span>
    </div>

    <div class="snav__scroll">
      <ul class="snav__list">
        <li v-for="item in items" :key="item.key">
          <button
            type="button"
            class="snav__item"
            :class="{ 'is-active': item.key === activeTab }"
            :aria-current="item.key === activeTab ? 'page' : undefined"
            :aria-label="ariaLabel(item)"
            :title="item.label"
            @click="go(item)"
          >
            <span class="snav__icon">
              <el-icon><component :is="item.icon" /></el-icon>
              <NavBadge class="snav__badge-dot" :count="badge(item)" dot />
            </span>
            <span class="snav__label">{{ item.label }}</span>
            <NavBadge
              class="snav__badge"
              :class="{ 'snav__badge--hot': item.key === 'approvals' }"
              :count="badge(item)"
            />
          </button>
        </li>
        <!-- 태블릿(접힌 레일)에서는 아래 바로가기 목록이 숨는다. 대신 바로가기 화면으로 가는 아이콘 하나. -->
        <li class="only-tablet">
          <button
            type="button"
            class="snav__item"
            :class="{ 'is-active': activeTab === 'links' }"
            aria-label="바로가기"
            title="바로가기"
            @click="go({ to: '/links' })"
          >
            <span class="snav__icon"><el-icon><Link /></el-icon></span>
          </button>
        </li>
      </ul>

      <!--
        미니 캘린더 — 날짜 점프 전용.
        일부러 "무슨 일정이 있는 날" 점을 찍지 않는다. 레일은 아젠다를 조회하지
        않고, 스토어의 agendaRanges 는 범위+스코프 조합으로 키가 잡혀 있어서
        여기서 되읽으면 화면에 보이는 것과 다른 스코프의 데이터를 점으로 찍게 된다.
        틀린 점을 찍느니 안 찍는 편이 낫다.
      -->
      <div class="snav__wide">
        <div class="snav__divider" />
        <QuickLinksRail />
        <div class="snav__divider" />
        <div class="mcal">
          <div class="mcal__head">
            <span class="mcal__title u-num">{{ cursorLabel }}</span>
            <span class="mcal__nav">
              <button type="button" class="mcal__btn" aria-label="이전 달" @click="shiftMonth(-1)">
                <el-icon><ArrowLeft /></el-icon>
              </button>
              <button type="button" class="mcal__btn" aria-label="다음 달" @click="shiftMonth(1)">
                <el-icon><ArrowRight /></el-icon>
              </button>
            </span>
          </div>
          <div class="mcal__grid" role="grid">
            <span
              v-for="(w, i) in weekLabels"
              :key="w"
              class="mcal__weekday"
              :class="{ 'is-sun': i === 0, 'is-sat': i === 6 }"
            >{{ w }}</span>
            <button
              v-for="d in flatDays"
              :key="d.key"
              type="button"
              class="mcal__day u-num"
              :class="{ 'is-dim': !d.inMonth, 'is-today': d.isToday, 'is-selected': d.isSelected }"
              :aria-label="d.aria"
              @click="goDate(d.date)"
            >{{ d.n }}</button>
          </div>
        </div>

        <div class="snav__divider" />

        <div class="snav__legend">
          <span class="snav__legend-title">일정 종류</span>
          <span v-for="t in eventTypes" :key="t.value" class="snav__legend-item">
            <span class="snav__swatch" :style="{ background: t.color }" />{{ t.label }}
          </span>
        </div>
      </div>
    </div>

    <el-dropdown class="snav__account" trigger="click" placement="top-start" @command="onCommand">
      <button type="button" class="snav__me" aria-label="내 계정">
        <el-avatar :size="30" :src="avatar">{{ initial }}</el-avatar>
        <span class="snav__me-text">
          <span class="snav__me-name u-trunc">{{ user?.nickname || '사용자' }}</span>
          <span class="snav__me-sub u-trunc">{{ subtitle }}</span>
        </span>
        <el-icon class="snav__me-caret"><ArrowDown /></el-icon>
      </button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item command="profile"><el-icon><User /></el-icon> 내 프로필</el-dropdown-item>
          <el-dropdown-item command="locations"><el-icon><Location /></el-icon> 장소 관리</el-dropdown-item>
          <template v-if="canManageOrg">
            <el-dropdown-item command="org" divided><el-icon><Share /></el-icon> 조직도</el-dropdown-item>
            <el-dropdown-item command="admin"><el-icon><Setting /></el-icon> 관리자</el-dropdown-item>
          </template>
          <el-dropdown-item command="logout" divided><el-icon><SwitchButton /></el-icon> 로그아웃</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </nav>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { confirmLogout } from '@/utils/logout'
import { ArrowLeft, ArrowRight, ArrowDown, User, Location, Share, Setting, SwitchButton, Link } from '@element-plus/icons-vue'
import NavBadge from './NavBadge.vue'
import QuickLinksRail from './QuickLinksRail.vue'
import { visibleNavItems, navBadgeCount, navAriaLabel } from './navItems'
import { buildMonthWeeks } from '@/composables/useMonthGrid'
import { ymd } from '@/utils/agendaItem'
import { EVENT_TYPE_LIST } from '@/utils/eventTypes'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'

const WEEK_LABELS = ['일', '월', '화', '수', '목', '금', '토']

export default {
  name: 'SideNav',
  components: { NavBadge, QuickLinksRail, ArrowLeft, ArrowRight, ArrowDown, User, Location, Share, Setting, SwitchButton, Link },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    const items = computed(() => visibleNavItems(store.getters, 'rail'))
    const activeTab = computed(() => route.meta?.tab || '')
    const eventTypes = EVENT_TYPE_LIST

    const user = computed(() => store.state.user)
    const avatar = computed(() => getProfileImageUrl(user.value))
    const initial = computed(() => getProfileInitial(user.value))
    const canManageOrg = computed(() => store.getters.isAdmin)
    const subtitle = computed(() => {
      const parts = []
      if (user.value?.department?.name) parts.push(user.value.department.name)
      if (user.value?.position?.name) parts.push(user.value.position.name)
      return parts.join(' · ') || '프로필'
    })

    // ---- 미니 캘린더 ----
    const todayYmd = ymd(new Date())
    const cursor = ref(new Date())

    // 캘린더 화면에서 달을 넘기면 레일도 따라간다.
    // 반대 방향(레일 → 캘린더)은 goDate 가 담당한다.
    watch(
      () => route.query.date,
      (v) => {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v || '')) cursor.value = new Date(`${v}T00:00:00`)
      },
      { immediate: true }
    )

    const cursorLabel = computed(
      () => `${cursor.value.getFullYear()}년 ${cursor.value.getMonth() + 1}월`
    )

    const flatDays = computed(() => {
      const month = cursor.value.getMonth()
      const selected = /^\d{4}-\d{2}-\d{2}$/.test(route.query.date || '') ? route.query.date : ''
      return buildMonthWeeks(cursor.value).flat().map((date) => {
        const key = ymd(date)
        return {
          key,
          date,
          n: date.getDate(),
          inMonth: date.getMonth() === month,
          isToday: key === todayYmd,
          isSelected: key === selected && key !== todayYmd,
          aria: `${date.getMonth() + 1}월 ${date.getDate()}일`
        }
      })
    })

    function shiftMonth(dir) {
      const d = new Date(cursor.value)
      // 1일로 맞추고 옮긴다. 31일에서 -1 하면 2월을 건너뛰어 3월로 간다.
      d.setDate(1)
      d.setMonth(d.getMonth() + dir)
      cursor.value = d
    }

    function goDate(date) {
      const target = ymd(date)
      const onCalendar = route.path.startsWith('/calendar')
      const query = onCalendar
        ? { ...route.query, date: target }
        : { view: 'month', date: target }
      router
        .push({ path: onCalendar ? route.path : '/calendar/month', query })
        .catch(() => {})
    }

    // ---- nav ----
    function badge(item) {
      return navBadgeCount(item, store.getters)
    }
    function ariaLabel(item) {
      return navAriaLabel(item, badge(item))
    }
    function go(item) {
      if (route.path === item.to) return
      router.push(item.to).catch(() => {})
    }

    function onCommand(cmd) {
      const routes = {
        profile: '/profile',
        locations: '/locations',
        org: '/admin/org',
        admin: '/admin'
      }
      if (routes[cmd]) {
        router.push(routes[cmd]).catch(() => {})
        return
      }
      if (cmd === 'logout') confirmLogout(store)
    }

    return {
      items, activeTab, eventTypes, badge, ariaLabel, go,
      user, avatar, initial, subtitle, canManageOrg, onCommand,
      weekLabels: WEEK_LABELS, cursorLabel, flatDays, shiftMonth, goDate
    }
  }
}
</script>

<style scoped>
/*
 * 좌측 레일은 잉크(--color-ink)다.
 *
 * 흰 레일 + 흰 캔버스였을 때는 1px 경계선 하나가 앱 크롬과 콘텐츠를 가르는
 * 유일한 신호였고, 그래서 화면 전체가 "설정 페이지" 처럼 읽혔다.
 * 어두운 레일은 눈이 콘텐츠로 먼저 가게 만들고, 배지(특히 결재 대기 빨강)가
 * 훨씬 멀리서도 보인다.
 */
.snav {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: var(--z-topbar); /* 상단바보다 위 — 브랜드 영역이 겹치지 않게 */
  width: var(--nav-side-w-collapsed);
  display: flex;
  flex-direction: column;
  background: var(--color-ink);
  border-right: 1px solid var(--color-ink-line);
  /* NavBadge 가 아이콘 위에 겹칠 때 두르는 링 색 (기본값은 흰 표면) */
  --nav-badge-ring: var(--color-ink);
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
  padding-left: var(--safe-left);
  overflow: hidden;
}

/* ---------- 브랜드 ---------- */
.snav__brand {
  height: var(--topbar-h);
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 20px;
  border-bottom: 1px solid var(--color-ink-line);
  flex-shrink: 0;
  white-space: nowrap;
}

.snav__mark {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: var(--r-md);
  background: var(--color-primary);
  color: var(--color-on-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-md);
  font-weight: var(--fw-bold);
  letter-spacing: -0.02em;
}

.snav__wordmark {
  display: none;
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-on-accent);
  letter-spacing: 0.01em;
}
.snav__wordmark span { color: var(--color-ink-text-dim); font-weight: var(--fw-regular); }

/* ---------- 스크롤 영역 ----------
   세로가 짧은 노트북에서 미니 캘린더 + 범례가 계정 영역을 밀어내지 않게 한다. */
.snav__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
}
.snav__scroll::-webkit-scrollbar { width: 0; }

/* ---------- 메뉴 ---------- */
.snav__list {
  list-style: none;
  margin: 0;
  padding: var(--sp-3) var(--sp-3) var(--sp-2);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.snav__item {
  width: 100%;
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 0 10px;
  border: 0;
  border-radius: var(--r-md);
  background: none;
  color: var(--color-ink-text);
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-regular);
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
}

.snav__item:hover { background: var(--color-ink-2); color: var(--color-on-accent); }

.snav__item.is-active {
  background: var(--color-primary);
  color: var(--color-on-accent);
  font-weight: var(--fw-medium);
}

.snav__icon {
  position: relative;
  font-size: var(--ic-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--ic-md);
  color: var(--color-ink-text-dim);
}
.snav__item:hover .snav__icon,
.snav__item.is-active .snav__icon { color: var(--color-on-accent); }

.snav__badge-dot { position: absolute; top: -2px; right: -4px; }
.snav__label { display: none; flex: 1; text-align: left; }

.snav__badge {
  display: none;
  margin-left: auto;
  background: var(--color-ink-line-2);
  color: var(--color-ink-text);
}
.snav__badge--hot { background: var(--color-danger); color: var(--color-on-accent); }
.snav__item.is-active .snav__badge {
  background: var(--color-on-accent-veil);
  color: var(--color-on-accent);
}

.snav__divider {
  height: 1px;
  background: var(--color-ink-line);
  margin: var(--sp-2) var(--sp-3);
}

.snav__wide { display: none; }

/* ---------- 미니 캘린더 ---------- */
.mcal { padding: 2px var(--sp-4) var(--sp-2); }

.mcal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2px var(--sp-2);
}

.mcal__title {
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-ink-text-bright);
}

.mcal__nav { display: flex; gap: 2px; }

.mcal__btn {
  width: 22px;
  height: 22px;
  border: 0;
  background: none;
  border-radius: var(--r-sm);
  color: var(--color-ink-text-dim);
  font-size: var(--fs-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.mcal__btn:hover { background: var(--color-ink-2); color: var(--color-on-accent); }

.mcal__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.mcal__weekday {
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-3xs);
  font-weight: var(--fw-medium);
  color: var(--color-ink-text-dim);
}
.mcal__weekday.is-sun { color: var(--ink-cal-sun); }
.mcal__weekday.is-sat { color: var(--ink-cal-sat); }

.mcal__day {
  height: 24px;
  border: 0;
  background: none;
  border-radius: var(--r-full);
  color: var(--ink-cal-day);
  font-size: var(--fs-xs);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mcal__day:hover { background: var(--color-ink-2); color: var(--color-on-accent); }
.mcal__day.is-dim { color: var(--ink-cal-dim); }
.mcal__day.is-selected { background: var(--color-ink-line-2); color: var(--color-on-accent); font-weight: var(--fw-semibold); }
.mcal__day.is-today { background: var(--color-primary); color: var(--color-on-accent); font-weight: var(--fw-semibold); }

/* ---------- 범례 ---------- */
.snav__legend {
  padding: 2px var(--sp-4) var(--sp-3);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px var(--sp-1);
}

.snav__legend-title {
  grid-column: 1 / -1;
  font-size: var(--fs-2xs);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.09em;
  color: var(--color-ink-text-dim);
  text-transform: uppercase;
  padding-bottom: var(--sp-1);
}

.snav__legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 3px 0;
  font-size: var(--fs-sm);
  color: var(--color-ink-text);
}

.snav__swatch {
  width: 9px;
  height: 9px;
  border-radius: var(--r-xs);
  flex-shrink: 0;
}

/* ---------- 계정 ---------- */
.snav__account { flex-shrink: 0; }

.snav__me {
  width: 100%;
  min-height: var(--tap-min);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: var(--sp-3);
  border: 0;
  border-top: 1px solid var(--color-ink-line);
  background: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
}
.snav__me:hover { background: var(--color-ink-2); }

.snav__me :deep(.el-avatar) {
  background: var(--color-ink-line-2);
  color: var(--color-ink-text);
  border-color: var(--color-ink-line-2);
  flex-shrink: 0;
}

.snav__me-text { display: none; flex-direction: column; min-width: 0; flex: 1; }
.snav__me-name { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--color-ink-text-bright); }
.snav__me-sub { font-size: var(--fs-xs); color: var(--color-ink-text-dim); }
.snav__me-caret { display: none; color: var(--color-ink-text-faint); flex-shrink: 0; }

/* ---------- 데스크톱: 아이콘 전용 → 펼친 형태 ---------- */
@media (min-width: 1024px) { /* --bp-tablet-max + 1 */
  .snav { width: var(--nav-side-w); }
  .snav__wordmark { display: inline; }
  .snav__label { display: inline; }
  .snav__badge { display: inline-flex; }
  .snav__badge-dot { display: none; }
  .snav__wide { display: block; }
  .snav__me-text { display: flex; }
  .snav__me-caret { display: inline-flex; }
}
</style>
